import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "grounded-ai-workflow-automation",
  kind: "horizontal",
  title: "Agentic Workflow Engineering",
  shortTitle: "Agentic Workflows",
  status: "draft",
  tier: "exploring",
  seoTitle: "Agentic Workflow Engineering | General Applications",
  seoDescription:
    "Retrieval-backed assistants with schema-constrained outputs, bounded tool execution, and human review on consequential steps.",
  summary:
    "Assistants and automations grounded in approved documents and system records, with schema-constrained outputs, scoped tool permissions, and explicit human review where the decision carries consequence.",
  image: "/images/blog/deterministic-controls-ai-agents-hero.webp",
  relatedCapabilitySlugs: ["intelligent-systems", "automation-operations"],
  relatedIndustrySlugs: [
    "enterprise",
    "healthcare",
    "agriculture",
    "media-entertainment",
  ],
  relatedPostSlugs: [
    "deterministic-controls-ai-agents",
    "state-machine-verification-ai-pipelines",
  ],
  engineering: [
    "Exploring retrieval-backed agent workflows with explicit task, tool, and state boundaries",
    "Prototype patterns for constrained tool use, structured outputs, and human approval",
    "Evaluation approaches for retrieval quality, tool execution, state transition, and exception handling",
    "Operational designs that retain source context, decision authority, and auditability around AI-assisted work",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "An Agent Is a Workflow With New Failure Modes",
    },
    {
      type: "paragraph",
      text: "Adding an agent does not remove the need to decide what information is authoritative, what action is permitted, or who approves a consequential change. It makes those boundaries more important. A useful agentic workflow decomposes the work, constrains its tools, records its state, and presents uncertainty or exceptions to the person responsible for the outcome.",
    },
    {
      type: "heading",
      level: 2,
      text: "The Useful Question Is What the Agent May Actually Do",
    },
    {
      type: "paragraph",
      text: "Some work benefits from retrieval, classification, drafting, extraction, comparison, or routing. Other work needs deterministic rules, direct human judgment, or a conventional interface. The design begins by separating those roles instead of treating an open-ended prompt as the system. That makes the workflow more observable, more testable, and easier to stop when the operating conditions change.",
    },
  ],
  phases: {
    study: {
      focus:
        "Identify the bounded task, authoritative sources, permitted actions, decision owner, error consequences, and non-AI alternatives before selecting an agent pattern.",
      produces: [
        "Task decomposition and decision-authority map",
        "Source, retrieval, tool, permission, and data-boundary inventory",
        "Failure-mode, escalation, and human-review analysis",
        "Feasibility criteria for a bounded prototype",
      ],
      fit: {
        good: [
          "Teams with a repeatable, evidence-backed workflow where retrieval, drafting, classification, comparison, or routing can assist a named human decision",
          "Organizations prepared to define tool permissions, evaluation criteria, and human authority before delegating any action to a model",
        ],
        poor: [
          "A request for an unrestricted agent to replace an undefined process or act without source, permission, or review boundaries",
          "A workflow whose risks cannot be safely contained while the system is still being evaluated",
        ],
      },
    },
    "initial-design": {
      focus:
        "Design the smallest bounded workflow that makes task state, source context, tool permissions, review, and exception paths explicit.",
      produces: [
        "Agent task, state, tool, and authority model",
        "Retrieval, prompt, output-schema, and validation architecture",
        "Human-review, escalation, and stop-condition design",
        "Prototype evaluation and observability plan",
      ],
    },
    "detailed-design": {
      focus:
        "Specify the tool contracts, validation rules, evaluation cases, interfaces, and operational controls needed to learn whether the workflow is safe and useful.",
      produces: [
        "Tool, retrieval, structured-output, and state-transition contracts",
        "Evaluation corpus, scenario set, and acceptance criteria",
        "Permission, logging, traceability, and exception-handling requirements",
        "Operator review and intervention interface specifications",
      ],
    },
    implementation: {
      focus:
        "Develop and evaluate a bounded prototype with real sources and reviewers, retaining the evidence needed to decide whether it should advance.",
      produces: [
        "Bounded agentic workflow prototype",
        "Observed task, tool, state, and exception records",
        "Evaluation findings, safeguards, and known limitations",
        "Recommendation to extend, redesign, hold, or stop the work",
      ],
    },
  },
};
